import { useState } from 'react';
import { Pencil, Plus, Trash2, Lock, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { createRole, updateRole, deleteRole, type RoleDto, type PermissionDef } from '../../services/auth.api';

interface Props {
  roles: RoleDto[];
  catalog: PermissionDef[];
  onChanged: () => void;
  onError: (message: string) => void;
}

type Draft = { id?: string; name: string; description: string; permissions: string[]; isSystem: boolean };

const errorOf = (err: unknown) => {
  const d = (err as { response?: { data?: { error?: string; errors?: { msg?: string }[] } } })?.response?.data;
  return d?.error ?? d?.errors?.[0]?.msg ?? 'Request failed';
};

export default function RolesPanel({ roles, catalog, onChanged, onError }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const openDraft = (d: Draft | null) => { setError(''); setDraft(d); };
  // users.manage is Admin-only (backend 400s otherwise), so it is never offered here.
  const editable = catalog.filter((p) => p.key !== 'users.manage');
  const groups = [...new Set(editable.map((p) => p.group))];

  const toggle = (key: string) => {
    if (!draft) return;
    const has = draft.permissions.includes(key);
    let next = has ? draft.permissions.filter((k) => k !== key) : [...draft.permissions, key];
    if (!has) {
      // A write needs its page: tick the view too.
      const req = catalog.find((p) => p.key === key)?.requires;
      if (req && !next.includes(req)) next = [...next, req];
    } else {
      // Unticking a view also unticks the writes that depend on it.
      next = next.filter((k) => catalog.find((p) => p.key === k)?.requires !== key);
    }
    setDraft({ ...draft, permissions: next });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const body = { name: draft.name, description: draft.description, permissions: draft.permissions };
      if (draft.id) await updateRole(draft.id, draft.isSystem ? { description: body.description, permissions: body.permissions } : body);
      else await createRole(body);
      openDraft(null);
      onChanged();
    } catch (err) {
      setError(errorOf(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role: RoleDto) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await deleteRole(role.id);
      onChanged();
    } catch (err) {
      onError(errorOf(err));
    }
  };

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4 flex flex-row items-center justify-between">
        <CardTitle>Roles</CardTitle>
        <button
          onClick={() => openDraft({ name: '', description: '', permissions: [], isSystem: false })}
          className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
        >
          <Plus className="w-4 h-4" /> New role
        </button>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="py-2 px-2 font-medium">Role</th>
              <th className="py-2 px-2 font-medium">Users</th>
              <th className="py-2 px-2 font-medium">Permissions</th>
              <th className="py-2 px-2" />
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 last:border-0">
                <td className="py-2 px-2">
                  <span className="font-medium text-gray-900">{r.name}</span>
                  {r.description && <p className="text-xs text-gray-500">{r.description}</p>}
                </td>
                <td className="py-2 px-2 text-gray-600">{r.userCount}</td>
                <td className="py-2 px-2 text-gray-600">{r.permissions.length} / {catalog.length}</td>
                <td className="py-2 px-2 text-right">
                  {r.name === 'Admin' ? (
                    <Lock className="inline w-4 h-4 text-gray-400" aria-label="Admin cannot be changed" />
                  ) : (
                    <div className="flex justify-end gap-2">
                      <button title="Edit role" onClick={() => openDraft({ ...r, permissions: [...r.permissions] })}
                        className="p-1.5 text-gray-500 hover:text-purple-600 rounded"><Pencil className="w-4 h-4" /></button>
                      {!r.isSystem && (
                        <button title="Delete role" onClick={() => remove(r)}
                          className="p-1.5 text-gray-500 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div role="dialog" aria-modal="true" className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{draft.id ? `Edit role: ${draft.name}` : 'New role'}</h3>
              <button onClick={() => openDraft(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <label className="block text-sm font-medium text-gray-700">
                Name
                <input value={draft.name} maxLength={50} disabled={draft.isSystem}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50" />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Description
                <input value={draft.description} maxLength={200} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </label>
              {groups.map((g) => (
                <fieldset key={g} className="border border-gray-200 rounded-lg p-3">
                  <legend className="px-1 text-xs font-semibold uppercase text-gray-500">{g}</legend>
                  {editable.filter((p) => p.group === g).map((p) => (
                    <label key={p.key} className="flex items-center gap-2 py-1 text-sm text-gray-700">
                      <input type="checkbox" checked={draft.permissions.includes(p.key)} onChange={() => toggle(p.key)} />
                      {p.label}
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
            {error && <p role="alert" className="px-4 pb-2 text-sm text-red-600">{error}</p>}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => openDraft(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              <button onClick={save} disabled={saving || draft.name.trim().length < 2}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
